import { Link } from 'react-router-dom';
import { V2Badge, V2Card, V2CardHeader, LoadingState } from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import type { JournalListResponse } from '../../lib/types/finance.js';
import { JOURNAL_STATUS_LABEL } from '../../lib/types/finance.js';
import { journalHref } from '../../lib/finance-display.js';

/**
 * Related GL documents for an inventory movement. Matches server
 * `sourceReference` only — never invents a journal id.
 */
export function RelatedJournalsCard({
  sourceReferences,
  base,
  refreshKey,
}: {
  readonly sourceReferences: readonly string[];
  readonly base: string;
  readonly refreshKey?: number;
}) {
  const result = useApiGet<JournalListResponse>(`${base}/api/v1/accounting/journal-entries`, [
    base,
    refreshKey,
  ]);
  const refs = new Set(sourceReferences.filter(Boolean));
  const matches =
    result.status === 'ready'
      ? result.data.items.filter(
          (row) => row.sourceReference !== null && refs.has(row.sourceReference),
        )
      : [];

  return (
    <V2Card>
      <V2CardHeader title="سند حسابداری مرتبط" />
      {result.status === 'loading' ? <LoadingState /> : null}
      {result.status === 'error' ? (
        <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--sk-fg-muted)' }}>
          {result.error.status === 403
            ? 'مشاهدهٔ سند حسابداری این حرکت نیازمند دسترسی مالی است.'
            : 'سند حسابداری قابل نمایش نیست.'}
        </div>
      ) : null}
      {result.status === 'ready' ? (
        matches.length === 0 ? (
          <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--sk-fg-muted)' }}>
            سند حسابداری مرتبطی با این حرکت پیدا نشد.
          </div>
        ) : (
          <div className="sk-table-scroll">
            <table className="sk-table sk-table--compact">
              <thead>
                <tr>
                  <th>شماره</th>
                  <th>تاریخ</th>
                  <th>وضعیت</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((row) => (
                  <tr key={row.journalId}>
                    <td>
                      <Link
                        className="ltr sk-num"
                        to={journalHref(base, row.journalId)}
                        style={{ fontFamily: 'var(--sk-font-mono)' }}
                      >
                        {row.officialDocumentNumber ?? row.provisionalNumber}
                      </Link>
                    </td>
                    <td className="sk-num">{row.businessDate}</td>
                    <td>
                      <V2Badge tone={row.status === 'posted' ? 'success' : 'neutral'}>
                        {JOURNAL_STATUS_LABEL[row.status] ?? 'وضعیت ثبت‌شده'}
                      </V2Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </V2Card>
  );
}
